import { useState, lazy, Suspense } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAdminQuote, useReviewQuote, useAdminMaterials } from '@/hooks/useAdmin';
import { useToast } from '@/contexts/ToastContext';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { isPreviewableFile } from '@/components/upload/ModelViewer';
import { QUOTE_STATUS_COLORS } from '@/components/admin/status-colors';

const ModelViewer = lazy(() =>
  import('@/components/upload/ModelViewer').then((m) => ({ default: m.ModelViewer })),
);

// ---------------------------------------------
// Main component
// ---------------------------------------------

export const AdminQuoteReviewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: quote, isLoading, error } = useAdminQuote(id ?? null);
  const { data: materials } = useAdminMaterials();
  const reviewMutation = useReviewQuote();

  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showPricingForm, setShowPricingForm] = useState(false);
  const [manualPrices, setManualPrices] = useState<
    { materialId: string; price: string; estimatedDays: string }[]
  >([]);

  const isReviewable = quote?.status === 'review' || quote?.status === 'processing';

  const handleApprove = async () => {
    if (!id) return;

    const prices = showPricingForm && manualPrices.length > 0
      ? manualPrices
          .filter((p) => p.price && parseFloat(p.price) > 0)
          .map((p) => ({
            materialId: p.materialId,
            price: parseFloat(p.price),
            rawPrice: parseFloat(p.price),
            estimatedDays: parseInt(p.estimatedDays, 10) || 5,
          }))
      : undefined;

    try {
      await reviewMutation.mutateAsync({
        quoteId: id,
        input: {
          action: 'approve',
          adminReviewNotes: reviewNotes || undefined,
          manualPrices: prices,
        },
      });
      toast.success('Quote approved');
      navigate('/admin/quotes');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve quote');
    }
  };

  const handleReject = async () => {
    if (!id || !rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      await reviewMutation.mutateAsync({
        quoteId: id,
        input: {
          action: 'reject',
          rejectionReason: rejectionReason.trim(),
          adminReviewNotes: reviewNotes || undefined,
        },
      });
      toast.success('Quote rejected');
      navigate('/admin/quotes');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reject quote');
    }
  };

  const addPriceRow = () => {
    setManualPrices((prev) => [...prev, { materialId: '', price: '', estimatedDays: '5' }]);
  };

  const updatePriceRow = (index: number, field: string, value: string) => {
    setManualPrices((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const removePriceRow = (index: number) => {
    setManualPrices((prev) => prev.filter((_, i) => i !== index));
  };

  // Loading / error states
  if (isLoading) {
    return (
      <div style={{ paddingTop: 'var(--space-12)', paddingBottom: 'var(--space-12)' }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div style={{ padding: 'var(--space-8)' }}>
        <p style={{ color: 'var(--color-error-500)', fontSize: 'var(--text-sm)' }}>
          Quote not found or failed to load.
        </p>
        <Link
          to="/admin/quotes"
          style={{ fontSize: 'var(--text-sm)', color: 'var(--text-accent)', marginTop: 'var(--space-2)', display: 'inline-block' }}
        >
          Back to quotes
        </Link>
      </div>
    );
  }

  const statusColors = QUOTE_STATUS_COLORS[quote.status] ?? QUOTE_STATUS_COLORS.expired;
  const canPreview = isPreviewableFile(quote.fileName) && quote.downloadUrl;

  return (
    <div style={{ maxWidth: '960px' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <button
          onClick={() => navigate('/admin/quotes')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-tertiary)',
            fontSize: 'var(--text-sm)',
            cursor: 'pointer',
            padding: 0,
            marginBottom: 'var(--space-2)',
          }}
        >
          &larr; Back to Quotes
        </button>
        <div className="flex items-center" style={{ gap: 'var(--space-3)' }}>
          <h1
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            Quote Review
          </h1>
          <span
            style={{
              borderRadius: 'var(--radius-full)',
              padding: 'var(--space-1) var(--space-3)',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-medium)',
              backgroundColor: statusColors.bg,
              color: statusColors.text,
              textTransform: 'capitalize',
            }}
          >
            {quote.status}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        {/* File Info Card */}
        <Card title="File Details">
          <div className="grid sm:grid-cols-2" style={{ gap: 'var(--space-4)' }}>
            <InfoField label="File Name" value={quote.fileName} />
            <InfoField label="File Type" value={quote.fileType ?? 'Unknown'} />
            <InfoField label="Quote ID" value={quote.quoteId} mono />
            <InfoField label="User ID" value={quote.userId} mono />
            <InfoField label="Created" value={new Date(quote.createdAt).toLocaleString()} />
            <InfoField label="Expires" value={new Date(quote.expiresAt).toLocaleString()} />
            {quote.fileSizeBytes && (
              <InfoField
                label="File Size"
                value={`${(quote.fileSizeBytes / (1024 * 1024)).toFixed(2)} MB`}
              />
            )}
          </div>
          {quote.downloadUrl && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <a
                href={quote.downloadUrl}
                download={quote.fileName}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-accent)',
                  textDecoration: 'none',
                }}
              >
                Download Model File
              </a>
            </div>
          )}
        </Card>

        {/* 3D Preview */}
        {canPreview && (
          <Card title="3D Preview">
            <Suspense fallback={<LoadingSpinner size="lg" />}>
              <ModelViewer
                url={quote.downloadUrl}
                fileName={quote.fileName}
                className="h-[400px]"
              />
            </Suspense>
          </Card>
        )}

        {/* Geometry / Analysis Card */}
        {(quote.volumeCm3 > 0 || quote.boundingBox) && (
          <Card title="Model Analysis">
            <div className="grid sm:grid-cols-3" style={{ gap: 'var(--space-4)' }}>
              <InfoField label="Volume" value={`${quote.volumeCm3} cm3`} />
              <InfoField label="Surface Area" value={`${quote.surfaceAreaCm2} cm2`} />
              {quote.boundingBox && (
                <InfoField
                  label="Bounding Box"
                  value={`${(quote.boundingBox.width * 10).toFixed(1)} x ${(quote.boundingBox.height * 10).toFixed(1)} x ${(quote.boundingBox.depth * 10).toFixed(1)} mm`}
                />
              )}
            </div>
          </Card>
        )}

        {/* Auto-analysis not available message */}
        {quote.volumeCm3 === 0 && !quote.boundingBox && (
          <Card title="Model Analysis">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', margin: 0 }}>
              Auto-analysis is not available for this file type. Download and inspect the model manually, then set pricing below.
            </p>
          </Card>
        )}

        {/* Existing Prices */}
        {quote.prices.length > 0 && (
          <Card title="Current Pricing">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3" style={{ gap: 'var(--space-3)' }}>
              {quote.prices.map((p) => {
                const mat = materials?.find((m) => m.materialId === p.materialId);
                return (
                  <div
                    key={p.materialId}
                    style={{
                      padding: 'var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-default)',
                      backgroundColor: 'var(--bg-surface-sunken)',
                    }}
                  >
                    <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', margin: 0 }}>
                      {mat?.name ?? p.materialId}
                    </p>
                    <p style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-success-600)', margin: 'var(--space-1) 0 0' }}>
                      ${p.price.toFixed(2)}
                    </p>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', margin: 'var(--space-1) 0 0' }}>
                      ~{p.estimatedDays} business days
                    </p>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Admin Notes */}
        {quote.adminReviewNotes && (
          <Card title="Admin Notes">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0, whiteSpace: 'pre-wrap' }}>
              {quote.adminReviewNotes}
            </p>
          </Card>
        )}

        {/* Rejection Reason */}
        {quote.rejectionReason && (
          <Card title="Rejection Reason">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error-700)', margin: 0 }}>
              {quote.rejectionReason}
            </p>
          </Card>
        )}

        {/* Review Actions */}
        {isReviewable && (
          <Card title="Review Actions">
            {/* Notes field */}
            <div>
              <label
                htmlFor="review-notes"
                style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text-secondary)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                Admin Notes (internal)
              </label>
              <textarea
                id="review-notes"
                rows={3}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                className="admin-input"
                placeholder="Internal notes about this quote (not shown to customer)"
                style={{
                  display: 'block',
                  width: '100%',
                  resize: 'vertical',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-default)',
                  padding: 'var(--space-2) var(--space-3)',
                  fontSize: 'var(--text-sm)',
                  backgroundColor: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  outline: 'none',
                }}
              />
            </div>

            {/* Manual pricing toggle */}
            <div style={{ marginTop: 'var(--space-4)' }}>
              <button
                type="button"
                onClick={() => {
                  setShowPricingForm((prev) => !prev);
                  if (!showPricingForm && manualPrices.length === 0) {
                    addPriceRow();
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-accent)',
                  fontSize: 'var(--text-sm)',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline',
                }}
              >
                {showPricingForm ? 'Hide manual pricing' : 'Set manual pricing'}
              </button>
            </div>

            {/* Manual pricing form */}
            {showPricingForm && (
              <div
                style={{
                  marginTop: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-default)',
                  backgroundColor: 'var(--bg-surface-sunken)',
                }}
              >
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', margin: '0 0 var(--space-3)' }}>
                  Set prices per material for this quote. These override any auto-calculated prices.
                </p>

                {manualPrices.map((row, i) => (
                  <div
                    key={i}
                    className="flex items-end"
                    style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}
                  >
                    <div style={{ flex: 2 }}>
                      {i === 0 && (
                        <label style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                          Material
                        </label>
                      )}
                      <select
                        value={row.materialId}
                        onChange={(e) => updatePriceRow(i, 'materialId', e.target.value)}
                        className="admin-input"
                        style={{
                          display: 'block',
                          width: '100%',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--border-default)',
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--text-sm)',
                          backgroundColor: 'var(--bg-surface)',
                          color: 'var(--text-primary)',
                        }}
                      >
                        <option value="">Select material</option>
                        {materials?.filter((m) => m.isActive).map((m) => (
                          <option key={m.materialId} value={m.materialId}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      {i === 0 && (
                        <label style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                          Price ($)
                        </label>
                      )}
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={row.price}
                        onChange={(e) => updatePriceRow(i, 'price', e.target.value)}
                        className="admin-input"
                        placeholder="0.00"
                        style={{
                          display: 'block',
                          width: '100%',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--border-default)',
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--text-sm)',
                          backgroundColor: 'var(--bg-surface)',
                          color: 'var(--text-primary)',
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      {i === 0 && (
                        <label style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                          Days
                        </label>
                      )}
                      <input
                        type="number"
                        min="1"
                        value={row.estimatedDays}
                        onChange={(e) => updatePriceRow(i, 'estimatedDays', e.target.value)}
                        className="admin-input"
                        style={{
                          display: 'block',
                          width: '100%',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--border-default)',
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--text-sm)',
                          backgroundColor: 'var(--bg-surface)',
                          color: 'var(--text-primary)',
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removePriceRow(i)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-error-500)',
                        fontSize: 'var(--text-lg)',
                        cursor: 'pointer',
                        padding: 'var(--space-2)',
                        lineHeight: 1,
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addPriceRow}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-accent)',
                    fontSize: 'var(--text-sm)',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  + Add material price
                </button>
              </div>
            )}

            {/* Reject form */}
            {showRejectForm && (
              <div style={{ marginTop: 'var(--space-4)' }}>
                <label
                  htmlFor="rejection-reason"
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--color-error-700)',
                    marginBottom: 'var(--space-1)',
                  }}
                >
                  Rejection Reason (shown to customer)
                </label>
                <textarea
                  id="rejection-reason"
                  rows={2}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="admin-input"
                  placeholder="Explain why this quote is being rejected..."
                  style={{
                    display: 'block',
                    width: '100%',
                    resize: 'vertical',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-error-300)',
                    padding: 'var(--space-2) var(--space-3)',
                    fontSize: 'var(--text-sm)',
                    backgroundColor: 'var(--bg-surface)',
                    color: 'var(--text-primary)',
                    outline: 'none',
                  }}
                />
              </div>
            )}

            {/* Action buttons */}
            <div className="flex" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-5)' }}>
              <Button
                onClick={handleApprove}
                disabled={reviewMutation.isPending}
              >
                {reviewMutation.isPending ? 'Processing...' : 'Approve Quote'}
              </Button>
              {!showRejectForm ? (
                <Button
                  variant="ghost"
                  onClick={() => setShowRejectForm(true)}
                >
                  Reject
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={handleReject}
                    disabled={reviewMutation.isPending || !rejectionReason.trim()}
                    style={{ color: 'var(--color-error-600)' }}
                  >
                    Confirm Rejection
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------
// Shared components
// ---------------------------------------------

const Card = ({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}) => (
  <div
    style={{
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border-default)',
      backgroundColor: 'var(--bg-surface)',
      padding: 'var(--space-5)',
    }}
  >
    <h2
      style={{
        fontSize: 'var(--text-base)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--text-primary)',
        margin: '0 0 var(--space-4)',
      }}
    >
      {title}
    </h2>
    {children}
  </div>
);

const InfoField = ({
  label,
  value,
  mono,
}: {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}) => (
  <div>
    <p
      style={{
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-medium)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--text-tertiary)',
        margin: 0,
      }}
    >
      {label}
    </p>
    <p
      style={{
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--text-primary)',
        margin: 'var(--space-1) 0 0',
        fontFamily: mono ? 'monospace' : 'inherit',
        wordBreak: 'break-all',
      }}
    >
      {value}
    </p>
  </div>
);
